import React, { useState } from 'react';

import AvatarSkeleton from '@atlaskit/avatar/skeleton';
import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { UnitTileProps, UnitTileSize } from './types';
import { getAbbreviation, getVariant } from './utils';

// ─── Shared ──────────────────────────────────────────────────────────────────
const boxStyles = cssMap({
	xsmall: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		width: '20px',
		height: '20px',
		borderRadius: token('radius.tile', '5px'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
	},
	small: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		width: '24px',
		height: '24px',
		borderRadius: token('radius.tile', '6px'),
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
	},
	medium: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		width: '32px',
		height: '32px',
		borderRadius: token('radius.tile', '8px'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
	},
	large: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		width: '48px',
		height: '48px',
		borderRadius: token('radius.tile', '12px'),
		font: token('font.heading.large'),
		fontWeight: token('font.weight.medium'),
	},
});

const FALLBACK_TESTID = 'unit-tile';

const sandboxUnitStyles = cssMap({
	grayscale: {
		filter: 'grayscale(1)',
		boxSizing: 'border-box',
		borderWidth: token('border.width', '1px'),
		borderStyle: 'solid',
		borderColor: token('color.border', '#091e4224'),
	},
});

// ─── UnitTileImage ────────────────────────────────────────────────────────────
const imageHighlightedStyles = cssMap({
	overlay: {
		position: 'relative',
		'&::after': {
			content: '""',
			position: 'absolute',
			inset: '0',
			backgroundColor: token('color.background.neutral.hovered'),
			borderRadius: 'inherit',
		},
	},
});

const imageLoadingStyles = cssMap({
	container: { position: 'relative', overflow: 'hidden' },
	overlay: { position: 'absolute', inset: '0' },
});

interface UnitTileLoadingSkeletonProps {
	size: UnitTileSize;
	testId: string;
}

function UnitTileLoadingSkeleton({ size, testId }: UnitTileLoadingSkeletonProps) {
	return (
		<Box
			xcss={cx(boxStyles[size], imageLoadingStyles.container)}
			testId={`${testId}-loading-skeleton`}
		>
			<AvatarSkeleton appearance="square" size={size} />
		</Box>
	);
}

function UnitTileImage({
	unitName,
	size,
	iconUrl,
	isHighlighted,
	isSandboxUnit,
	srcSet,
	testId,
	onError,
	onLoad,
	showLoadingState,
}: {
	iconUrl: string;
	isHighlighted?: boolean;
	isSandboxUnit?: boolean;
	onError: () => void;
	onLoad: () => void;
	showLoadingState: boolean;
	size: UnitTileSize;
	srcSet?: string;
	testId: string;
	unitName: string;
}): JSX.Element {
	return (
		<Box
			xcss={cx(
				boxStyles[size],
				imageLoadingStyles.container,
				isHighlighted ? imageHighlightedStyles.overlay : undefined,
				isSandboxUnit ? sandboxUnitStyles.grayscale : undefined,
			)}
			testId={testId}
		>
			<img
				src={iconUrl}
				srcSet={srcSet}
				alt={unitName}
				onError={onError}
				onLoad={onLoad}
				/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop -- borderRadius: inherit requires inline style; cannot use cssMap for inherited values */
				style={{
					width: '100%',
					height: '100%',
					objectFit: 'cover',
					borderRadius: 'inherit',
					opacity: showLoadingState ? 0 : 1,
				}}
			/>
			{showLoadingState && (
				<Box xcss={imageLoadingStyles.overlay} testId={`${testId}-loading-skeleton`}>
					<AvatarSkeleton appearance="square" size={size} />
				</Box>
			)}
		</Box>
	);
}

// ─── UnitTileAbbreviation ─────────────────────────────────────────────────────
const backgroundColorStyles = cssMap({
	purple: { backgroundColor: token('color.background.accent.purple.subtlest', '#f8eefe') },
	blue: { backgroundColor: token('color.background.accent.blue.subtlest', '#e9f2fe') },
	lime: { backgroundColor: token('color.background.accent.lime.subtlest', '#efffd6') },
	yellow: { backgroundColor: token('color.background.accent.yellow.subtlest', '#fef7c8') },
});

const highlightedBackgroundColorStyles = cssMap({
	purple: { backgroundColor: token('color.background.accent.purple.subtlest.hovered', '#eed7fc') },
	blue: { backgroundColor: token('color.background.accent.blue.subtlest.hovered', '#cfe1fd') },
	lime: { backgroundColor: token('color.background.accent.lime.subtlest.hovered', '#d3f1a7') },
	yellow: { backgroundColor: token('color.background.accent.yellow.subtlest.hovered', '#f5e989') },
});

const textColorStyles = cssMap({
	purple: { color: token('color.text.accent.purple.bolder', '#48245d') },
	blue: { color: token('color.text.accent.blue.bolder', '#123263') },
	lime: { color: token('color.text.accent.lime.bolder', '#37471f') },
	yellow: { color: token('color.text.accent.yellow.bolder', '#533f04') },
});

function UnitTileAbbreviation({
	unitName,
	size,
	isHighlighted,
	isSandboxUnit,
	testId,
}: {
	isHighlighted?: boolean;
	isSandboxUnit?: boolean;
	size: UnitTileSize;
	testId: string;
	unitName: string;
}): JSX.Element {
	const variant = getVariant(unitName);
	const displayInitials = getAbbreviation(unitName);

	return (
		<Box
			xcss={cx(
				boxStyles[size],
				backgroundColorStyles[variant],
				isHighlighted ? highlightedBackgroundColorStyles[variant] : undefined,
				isSandboxUnit ? sandboxUnitStyles.grayscale : undefined,
			)}
			testId={testId}
			data-variant={variant}
		>
			<Box as="span" xcss={textColorStyles[variant]}>
				{displayInitials}
			</Box>
		</Box>
	);
}

export function UnitTile({
	unitName,
	size,
	iconUrl,
	isHighlighted,
	isSandboxUnit,
	srcSet,
	testId,
	isIconLoading,
}: UnitTileProps): JSX.Element {
	const resolvedTestId = testId ?? FALLBACK_TESTID;
	// Signed icon URLs can expire after rendering; fall back to initials for a failed URL, while allowing a new URL to retry.
	const [failedIconUrl, setFailedIconUrl] = useState<string>();
	// Tracks native image loading
	const [loadedIconUrl, setLoadedIconUrl] = useState<string>();

	const isIconBehaviorEnabled = fg('platform_unit_tile_image_error_fallback');

	const hasFailedImage = failedIconUrl === iconUrl;
	const displayedIconUrl = isIconBehaviorEnabled && hasFailedImage ? undefined : iconUrl;
	const isImageLoading = Boolean(displayedIconUrl) && loadedIconUrl !== displayedIconUrl;
	const showSkeleton = isIconBehaviorEnabled && (isIconLoading || isImageLoading);

	if (!displayedIconUrl) {
		if (showSkeleton) {
			return <UnitTileLoadingSkeleton size={size} testId={resolvedTestId} />;
		}

		return (
			<UnitTileAbbreviation
				unitName={unitName}
				size={size}
				isHighlighted={isHighlighted}
				isSandboxUnit={isSandboxUnit}
				testId={resolvedTestId}
			/>
		);
	}

	return (
		<UnitTileImage
			unitName={unitName}
			size={size}
			iconUrl={displayedIconUrl}
			srcSet={srcSet}
			// Treat a failed image as no icon so the existing initials tile is rendered.
			onError={() => setFailedIconUrl(displayedIconUrl)}
			onLoad={() => setLoadedIconUrl(displayedIconUrl)}
			showLoadingState={showSkeleton}
			isHighlighted={isHighlighted}
			isSandboxUnit={isSandboxUnit}
			testId={resolvedTestId}
		/>
	);
}
